import { GlobalDataContext } from "@/app/context/global/globalDataContext";
import { CreatureDefinitionType } from "@/app/types/creatureDefinitionType";
import { Avatar, Badge, Box, Progress, useDisclosure } from "@chakra-ui/react";
import { ReactElement, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthenticationContext } from "@/app/context/authentication/authenticationContext";
import { GlobalDataUserContext } from "@/app/context/global/globalDataUserContext";
import { Source, Layer, MapRef, Marker } from 'react-map-gl';
import * as turf from '@turf/turf';
import { ChaseType } from "@/app/types/chaseType";
import { NotificationContext } from "@/app/context/notification/notificationContext";
import { doesZoneOverlapWithExisting, getDistance, insideGeojson, validatePolygonIfNeeded } from "@/app/helpers/mapHelper";
import { capitalizeFirstLetter } from "@/app/helpers/stringHelper";
import { ZoneType } from "@/app/types/zoneType";
import { captureCreatureMessage, creatureCloseMessage, finishGameNotConnectedMessage, firstEggMessage, firstFeatherMessage, loosehGameMessage, startGameMessage } from "@/app/context/notification/helper/anaDiscussionHelper";
import { FirebaseContext } from "@/app/context/firebase/firebaseContext";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { GameType } from "@/app/context/game/types/gameType";
import { buildGameUrl } from "@/app/helpers/buildUrlHelper";
import { TiStarFullOutline } from "react-icons/ti";
import FinishGameModal from "./layout/modal/finishGameModal";
import LooseGameModal from "./layout/modal/looseGameModal";
import LeaderBoardModal from "./layout/modal/leaderBoardZoneModal";
import { map } from "lodash";
import { FaEgg, FaFeather, FaStar } from "react-icons/fa6";
import { GiCupcake } from "react-icons/gi";
import { storeDataWithDateLocalStorageHelper } from "@/app/helpers/localStorageHelper";
import { PlacesType } from "./types/placesType";
import { v4 } from "uuid";
import { FaInfoCircle } from "react-icons/fa";
import { getCompletion } from "@/app/helpers/chatGPTHelper";
import CharacterTalkingQuestModal from "./layout/modal/characterTalkingQuesModalt";
import { storage } from "firebase-admin";
import { AllQuestModelConst } from "@/app/const/allQuestModelConst";
import { allInGameCharactersConst } from "@/app/const/allInGameCharactersConst";
import CharacterDetailQuestModal from "./layout/modal/characterDetailQuestModal";

export const GameContext = createContext({} as GameType);

export const GameProvider = (props:any) => {

    const {t} = useTranslation();
    const router = useRouter()
    const mapRef = useRef<MapRef | null>(null);
    const [gameKilometers, setGameKilometers] = useState<number>(0)
    const [chase, setChase] = useState<ChaseType>({}as ChaseType);
    const [insideZone, setInsideZone] = useState<string|undefined>();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [closeToMonster, setCloseToMonster] = useState(false);
    const [doResize, setDoResize] = useState(false);

    const [gotBonus, setGotBonus] = useState(true);
    const [useBonus, setUseBonus] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [mapboxLoaded, setMapboxLoaded] = useState(false);
    const [lastCheckTime, setLastCheckTime] = useState(0);
    const [interestingPlaces, setInterestingPlaces] = useState<PlacesType[]>([]);
    const [inFrontCharacter, setInFrontCharacter] = useState<string|null>(null);
    const [characterDetail, setCharacterDetail] = useState<string|null>(null);

    const { isOpen:isOpenInGameCharacter, onOpen: onOpenInGameCharacter, onClose: onCloseInGameCharacter } = useDisclosure()
    const { isOpen:isOpenInGameCharacterDetail, onOpen: onOpenInGameCharacterDetail, onClose: onCloseInGameCharacterDetail } = useDisclosure()

    const intervalMinutes = 5 

    const {
        language,
        location,
        chaseId,
        firstLocation,
        distanceLocation,
        isRaining,
    } = useContext(GlobalDataContext)
    
    const {
        user,
        cloudStorage
    } = useContext(AuthenticationContext)
    
    const {
        isGlobalDataUserLoading,
        myZones,
        setMyZones,
        addNewCreature,
        addKilometersReducer,
        addOrUpdateActionHistory,
        play,
        config,
        userInfo,
        allPlayersPositions,
        allItems,
        setAllItems,
        addNewEgg,
        addFood,
        anaDiscussionStatus,
        updateAnaDiscussionStatus,
        addUnblocked,
        myArtifacts
    } = useContext(GlobalDataUserContext)

    const {
        setDoVibrate,
        addMessageWithId,
        isRecentMessage,
        addModalToQueue,
        character
    } = useContext(NotificationContext)

    const {
        logAnalyticsEvent
    } = useContext(FirebaseContext)


    const showCharacterDetail = (characterId:string)=>{
        setCharacterDetail(characterId)
    }

    const hideCharacterDetail = ()=>{
        setCharacterDetail(null)
        onCloseInGameCharacterDetail()
    }

    useEffect(()=>{
      if ( characterDetail ){
        onOpenInGameCharacterDetail()
      }
    },[characterDetail])

    useEffect(() => {
        if (chase.status === "started") {
          // Met à jour le temps toutes les secondes
          const interval = setInterval(() => {
            const elapsed = 1200 - Math.floor((new Date().getTime() - chase.date) / 1000)
            setElapsedTime(elapsed);
            if ( elapsed <= 0 ) {
                looseChase()
            }
          }, 1000); // Mise à jour toutes les 1000 millisecondes (1 seconde)
    
          // Nettoyage de l'intervalle lors du démontage du composant
          return () => clearInterval(interval);
        }
      }, [chase.status, chase.date]);

    useEffect(() => {
        // Mettre à jour le style au premier rendu
        updateDarkMode();
        // Configurer un intervalle pour vérifier l'heure toutes les minutes
        const interval = setInterval(() => {
            updateDarkMode();
        }, 60000); // 60000 ms = 1 minute
    
        // Nettoyer l'intervalle quand le composant est démonté
        return () => clearInterval(interval);
    }, []);

    useEffect(()=>{
        if ( chaseId){
            setChase({
                id: chaseId,
                zoneId: "",    
                date : new Date().getTime(),
                status: "pending",
                creatures: [],
                coords: []
            })
            setInsideZone(undefined)
        }
    },[chaseId])

    useEffect(()=>{
        //if ( chase.status == "started"){
            if ( gameKilometers + distanceLocation >= 250){
                addKilometersReducer(gameKilometers + distanceLocation)
                setGameKilometers(0)
                addOrUpdateActionHistory("kilometer",gameKilometers + distanceLocation)
            }else{
                setGameKilometers(gameKilometers + distanceLocation)
            }
        //}
    },[distanceLocation])
    
    useEffect(()=>{
        if ( closeToMonster && chase.status == "started") {
            const interval = setInterval(() => {
                if ( config?.allowBeep) {play("beep01")}
            }, 1500);

            return () => clearInterval(interval);
        }
    },[closeToMonster, chase.status])

    useEffect(()=>{
        if ( useBonus && chase.status == "started") {
            setGotBonus(false)
            //play("fart01")
            const interval = setInterval(() => {
                setUseBonus(false)
            }, 10000);

            return () => clearInterval(interval);
        }
    },[useBonus])

    const geoJson = useMemo(() => {
        return myZones && myZones.filter(item=>item.coords && item.coords.length).map((zone, index) => {
            const polygon = zone.coords.map(coord => [coord.lng, coord.lat]);
            const geojson = {
                type: 'Feature',
                properties: { title: zone.name },
                geometry: {
                    type: 'Polygon',
                    coordinates: [[...polygon, polygon[0]]] // Fermer le polygone en revenant au point de départ
                }
            };
            return {zoneId: zone.id, geojson: geojson}
        })
    },[myZones])

    useEffect(() => {
        setCloseToMonster(false);
        if (chase.status === 'started') {
          chase.coords.forEach((coord, index) => {
            if (isPlayerNearby({latitude:location?.lat,longitude:location?.lng}, {latitude:coord.lat,longitude:coord.lng})) {                
                let creature = addNewCreature(null, undefined, isRaining?"AQUATIC":"NORMAL");
                setChase(prevChase => ({
                    ...prevChase,
                    creatures: [...prevChase.creatures, creature as CreatureDefinitionType]
                }));
                removeCoord(index);
                captureCreatureMessage(addMessageWithId,t, character?.translationKey)
            }
            if ( chase.bonus && chase.bonus.length) {
                if (isPlayerNearby({latitude:location?.lat,longitude:location?.lng}, {latitude:chase.bonus[0].lat,longitude:chase.bonus[0].lng},false)) {
                    setGotBonus(true)
                    play("ding01")
                    removeBonus();
                }
            }
          });
        }
        if ( chase && chase.status !== 'started' && allItems){
            let indexToRemove = -1
            let inFrontCharacterTemp = null
            allItems.forEach((item, index) => {
                if (isPlayerNearby({latitude:location?.lat,longitude:location?.lng}, {latitude:item.lat,longitude:item.lng},false,10)) {
                    if ( item.type == "eggs" ){
                        addNewEgg(isRaining?"AQUATIC":"NORMAL", true) 
                        if ( !anaDiscussionStatus?.gotFirstEgg ){
                            firstEggMessage( addMessageWithId, t)
                            updateAnaDiscussionStatus("SET_NEW_EGG", true)                    
                        }
                        indexToRemove = index
                    }
                    if ( item.type == "food" ){
                        addFood(10)
                        indexToRemove = index
                    }
                    if ( item.type == "feather" ){
                      addUnblocked("feather")
                      if ( !anaDiscussionStatus?.gotFeather ){
                        firstFeatherMessage( addMessageWithId, t)
                        updateAnaDiscussionStatus("SET_NEW_FEATHER", true)                    
                      }
                      indexToRemove = index
                    }
                    if (item.type == "character") {
                        inFrontCharacterTemp = item.character
                        if ( inFrontCharacter !== inFrontCharacterTemp ){
                          setInFrontCharacter(inFrontCharacterTemp as string)
                          onOpenInGameCharacter()
                        }
                    }
                    
                }
            });
            if ( inFrontCharacterTemp !== inFrontCharacter && !isOpenInGameCharacter){
                setInFrontCharacter(inFrontCharacterTemp)
            }
            if ( indexToRemove >= 0 ){
              const newItems = allItems.filter((_, i) => i !== indexToRemove);
                setAllItems(newItems);
                storeDataWithDateLocalStorageHelper(newItems)
            }

        }
        if ( chase && chase.status !== 'started' && interestingPlaces ){
          let placeToRemove = ""
          interestingPlaces.forEach((item, index) => {
              if (placeToRemove == "" && !item.discovered && isPlayerNearby({latitude:location?.lat,longitude:location?.lng}, {latitude:item.coordinates[1],longitude:item.coordinates[0]},false,10)) {
                  placeToRemove = item.name   
                  setInterestingPlaces(prevState => prevState.map((item) => {
                    if (item.name === placeToRemove) {
                        return { ...item, discovered: true };
                    }
                    return item;
                   }))
                   if ( user ) {
                      getCompletion(character?.name as string, location?.lat as number, location?.lng as number, item.name, language, user)
                      .then((message)=>{ 
                        if ( message ) {
                          addMessageWithId(null,message as string, "message", "unread", null)
                          logAnalyticsEvent("cultural", {});
                          addOrUpdateActionHistory("cultural", 1);
                        }                    
                      })
                    }else{
                      addMessageWithId(null,t("discussion.infoNotConnected"), "message", "unread", null)
                    }
              }
          });
      }
        geoJson && geoJson.map((geojson)=>{
            if ( insideGeojson(geojson.geojson,location)){
                setInsideZone(geojson.zoneId)
            }
            if ( insideZone == geojson.zoneId && !insideGeojson(geojson.geojson,location) ) {
                setInsideZone(undefined)
            }
        })
        /*
        myZones.map((zone, index) => {
            const polygon = zone.coords.map(coord => [coord.lng, coord.lat]);
            const geojson = {
                type: 'Feature',
                properties: { title: 'polygon' },
                geometry: {
                    type: 'Polygon',
                    coordinates: [[...polygon, polygon[0]]] // Fermer le polygone en revenant au point de départ
                }
            };
            if ( insideZone == zone.id && !insideGeojson(geojson,location) ) {
                setInsideZone(undefined)
            }
            if ( insideGeojson(geojson,location)){
                setInsideZone(zone.id)
            }
        })
            */
      }, [location]); // Déclenché lorsque la location change

      useEffect(()=>{
        if ( chase && chase.status!=="started" && mapRef && mapRef.current && mapboxLoaded) {
          const newPlaces:any[]=[]
          const now = Date.now();
          const minutesSinceLastCheck = (now - lastCheckTime) / (1000 * 60);
          //if (minutesSinceLastCheck >= intervalMinutes) {
            // Mettre à jour l'heure du dernier check
            setLastCheckTime(now);
      
            const poiLayers = ['poi_label', 'landmark', 'historic','place_label']; // Liste des sources potentielles

            // Parcourir plusieurs sources pour récupérer des points d'intérêt
            poiLayers.forEach(layer => {
              const features = mapRef?.current?.querySourceFeatures('composite', {
                sourceLayer: layer
              });
              // Parcourir les POIs récupérés pour chaque layer
              features && features.forEach((feature) => {
                if ( !feature.properties ) return;

                const isInterestingPlace =
                (layer === 'poi_label' &&
                  ['museum', 'monument', 'artwork', 'arts_and_entertainment','historic', 'memorial', 'historic_site', 'castle'].includes(
                    feature.properties.class
                  )) ||
                (layer === 'landmark' &&
                  ['monument', 'museum', 'historic'].includes(feature.properties.class)) ||
                (layer === 'historic' &&
                  ['memorial', 'monument', 'archaeological_site', 'battlefield'].includes(
                    feature.properties.class
                  ));

                if (isInterestingPlace && location) {
                  let featureCoords;
                  
                  // Gérer les diff érents types de géométrie
                  if (feature.geometry.type === 'Point') {
                    featureCoords = feature.geometry.coordinates;
                  } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
                    // Utiliser le centroïde pour les polygones
                    const centroid = turf.centroid(feature);
                    featureCoords = centroid.geometry.coordinates;
                  } else {
                    // Ignorer les autres types de géométrie
                    return;
                  }
                  
                  // Calculer la distance entre le joueur et le lieu
                  const playerPoint = turf.point([location?.lng, location?.lat]);
                  const featurePoint = turf.point(featureCoords);
                  const distance = turf.distance(playerPoint, featurePoint, { units: 'meters' });

                  const placeName = feature.properties.name 
                  
                  if ( placeName && newPlaces.findIndex(p=>p.name===placeName) === -1){
                    // Ajouter les informations du lieu au tableau
                    const newPlace = {
                      id: v4(),
                      name: placeName,
                      coordinates: featureCoords,
                      properties: feature.properties,
                    };
                    newPlaces.push(newPlace);
                  } 
                }
              });
            });

          // Après la boucle, filtrer les lieux pour éviter les doublons
          const uniqueNewPlaces = newPlaces.filter(newPlace => {
            // Vérifier si le lieu existe déjà dans 'interestingPlaces'
            return !interestingPlaces.some(existingPlace => {
              // Comparer par nom et coordonnées
              return existingPlace.name === newPlace.name ;
            });
          });

          // Mettre à jour 'interestingPlaces' avec les nouveaux lieux uniques
          if (uniqueNewPlaces.length > 0) {
            setInterestingPlaces(prevState => [...prevState, ...uniqueNewPlaces]);
          }

          //}

        }
      },[location,chase, mapboxLoaded, myZones])

      useEffect(()=>{
        if (chase.status === 'started' && chase.coords.length === 0) {
          winChase();
        }
      },[chase.coords])

    useEffect(()=>{
        if ( firstLocation){
        }
    },[firstLocation])


    useEffect(()=>{
        setDoResize(true)
        geoJson && geoJson.map((geojson)=>{
            if ( insideGeojson(geojson.geojson,location)){
                setInsideZone(geojson.zoneId)
            }
            if ( insideZone == geojson.zoneId && !insideGeojson(geojson.geojson,location) ) {
                setInsideZone(undefined)
            }
        })
/*
        myZones && myZones.map((zone, index) => {
            const polygon = zone.coords.map(coord => [coord.lng, coord.lat]);
            const geojson = {
                type: 'Feature',
                properties: { title: 'polygon' },
                geometry: {
                    type: 'Polygon',
                    coordinates: [[...polygon, polygon[0]]] // Fermer le polygone en revenant au point de départ
                }
            };
            if ( insideZone == zone.id && !insideGeojson(geojson,location) ) {
                setInsideZone(undefined)
            }
            if ( insideGeojson(geojson,location)){          
                setInsideZone(zone.id)
            }
        })
            */
    },[myZones, chaseId])

    useEffect(()=>{
        if ( doResize){
            setDoResize(false)
        }
    },[doResize])

    useEffect(()=>{
        if (chase.status=="started"){
            setDoResize(true)
        }
    },[chase])

    useEffect(() => {
        if ( chase.status=="started" && chase.bonus && chase.bonus.length > 0){
                const timer = setTimeout(() => {
                removeBonus(); // Appel de la fonction après 30 secondes
            }, 150000); // 30000 millisecondes = 30 secondes
        
            // Nettoyage du timer si le composant est démonté avant la fin des 30 secondes
            return () => clearTimeout(timer);
        }
        if ( chase.status=="started" && ( !chase.bonus || !chase.bonus.length )){
                const timer = setTimeout(() => {
                generateBonus(); // Appel de la fonction après 30 secondes
            }, 300000); // 30000 millisecondes = 30 secondes
        
            // Nettoyage du timer si le composant est démonté avant la fin des 30 secondes
            return () => clearTimeout(timer);
        }
      }, [chase.bonus]);

    useEffect(() => {
        if ( chase.status=="started"){            
                const timer = setTimeout(() => {
                generateBonus(); // Appel de la fonction après 30 secondes
            }, 30000); // 30000 millisecondes = 30 secondes
        
            // Nettoyage du timer si le composant est démonté avant la fin des 30 secondes
            return () => clearTimeout(timer);
        }
      }, [chase.status]);

    const winChase = ()=>{
        setChase(prevChase => ({...prevChase, status: 'finished'}));
        const zone = myZones.find(z => z.id === chase.zoneId);
        const elapsedTime = (new Date().getTime() - chase.date);
        if ( zone && zone.ownerId!=="generic" ){
            if ( 
                (zone.leaderboard && zone.leaderboard.length > 0 && elapsedTime < zone.leaderboard[zone.leaderboard.length-1].timestamp ) ||
                (!zone.leaderboard || !zone.leaderboard.length )
            ){       
                try{
                    cloudStorage?.updateLeaderboard(zone.id, {userId: user?.uid as string, userPhotoUrl: user?.photoURL as string, userName: userInfo.userName as string }, elapsedTime).then()
                }catch(e){
                    if ( process.env.NODE_ENV==="development" ) {
                        console.log(e)
                    }
                }              
            }
        }
        addModalToQueue({
            component: FinishGameModal,
            props: {},
            doConfetti: true,
            doVibrate: true,
            onShow: ()=>{play("win01")}
        })
        if ( zone && zone.ownerId!=="generic" ){
            addModalToQueue({
                component: LeaderBoardModal,
                props: {zoneId: chase.zoneId},
                doConfetti: false,
                doVibrate: false
            })
        }
        !user && finishGameNotConnectedMessage(addMessageWithId,t, character?.translationKey)
        
        router.push(buildGameUrl(language, 1, undefined), undefined, { shallow: true }).then()
    }

    const looseChase = ()=>{
        setChase(prevChase => ({...prevChase, status: 'finished'}));
        play("gameover")
        addModalToQueue({
            component: LooseGameModal,
            props: {},
            doConfetti: false,
            doVibrate: false,
            onShow: ()=>{}
        })
        loosehGameMessage(addMessageWithId,t, character?.translationKey)
        
        router.push(buildGameUrl(language, 1, undefined), undefined, { shallow: true }).then()
    }

    const updateDarkMode = () => {
        const hour = new Date().getHours();
        if (hour >= 18 || hour < 6) {
          setIsDarkMode(true);
        } else {
          setIsDarkMode(false);
        }
    };

    const isPlayerNearby = (location:any, coord:any, checkBetween:boolean=true, distanceToCheck:number=4) => {
        let distance = getDistance(location, coord);
       
        if ( distance >= distanceToCheck && distance < (distanceToCheck+2)) {
            setDoVibrate(true);
            if ( checkBetween ) {
              setCloseToMonster(true);
              if (config?.allowBeep) {play("beep01")}
              if (!isRecentMessage()) {
                  creatureCloseMessage(addMessageWithId,t, character?.translationKey)
              }
          }
        }

        return distance < distanceToCheck;
    };
    
    const removeCoord = (index:any) => {
        setChase(prevChase => ({...prevChase, coords: prevChase.coords.filter((_, i) => i !== index)}));
    };

    const generateRandomPoints = useCallback((zoneId: string, quantity: number = 4) => {
        let gameZone = myZones.find(z => z.id === zoneId);
        if (!gameZone) return;
      
        try {
          const polygon = gameZone.coords.map(coord => [coord.lng, coord.lat]);
          const polygonFeature = turf.polygon([[...polygon, polygon[0]]]);
          const bbox = turf.bbox(polygonFeature);
          const pointsInside: any[] = [];
          const minDistance = 0.01; // Distance minimale en degrés (~1 km, ajustez selon vos besoins)
          let limit = 0;
        
          while (pointsInside.length < quantity && limit < 25) {
            const points = turf.randomPoint(quantity - pointsInside.length, { bbox }).features;
        
            points.forEach(point => {
              if (turf.booleanPointInPolygon(point, polygonFeature)) {
                const isFarEnough = pointsInside.every(existingPoint => {
                  return turf.distance(point, existingPoint, { units: 'kilometers' }) >= minDistance;
                });
        
                if (isFarEnough) {
                  pointsInside.push(point);
                }
              }
            });
            limit++;
          }
        
          return pointsInside.map(point => ({
            lat: point.geometry.coordinates[1],
            lng: point.geometry.coordinates[0]
          }));
        }catch(e){

        }
      }, [myZones]);

    const startGame = useCallback(() => {
        const coords = generateRandomPoints(insideZone as string, 4);
        if ( !coords) return;

        setChase((prevChase) => ({
          ...prevChase,
          date: new Date().getTime(),
          coords: coords as any,
          zoneId: insideZone as string,
          status: "started"
        }));
        setGameKilometers(0);
        startGameMessage(addMessageWithId, t, character?.translationKey);
        addOrUpdateActionHistory("play", 1);
        logAnalyticsEvent("play", {});
        setUseBonus(false);
        setGotBonus(false);
        setElapsedTime(1200);
      }, [insideZone, character, generateRandomPoints, addMessageWithId, t, addOrUpdateActionHistory, logAnalyticsEvent, setUseBonus, setGotBonus]);

    const generateBonus = () => {        
        const coords = generateRandomPoints(insideZone as string,1)
        if ( !coords) return;

        if ( process.env.NODE_ENV==="development" ) {
            console.log("bonus", coords)
        }
        setChase(chasePrev=>({
            ...chasePrev,
            bonus: coords as any[],
        }))
        play("magic01")
    }

    const removeBonus = () => {
        setChase(chasePrev=>({...chasePrev,bonus:[]}))
    }

    /*const renderPolygons = (): ReactElement | undefined => {
        if (!myZones || myZones.length === 0) return undefined;
    
        return (
            <>
                {myZones.map((zone, index) => {
                    if (!zone || !zone.coords || zone.coords.length < 3) return null;
    
                    const polygon = zone.coords.map(coord => [coord.lng, coord.lat]);
                    const geojson = {
                        type: 'Feature',
                        properties: { title: 'polygon' },
                        geometry: {
                            type: 'Polygon',
                            coordinates: [[...polygon, polygon[0]]] // Fermer le polygone en revenant au point de départ
                        }
                    };

                    return (
                        <Source key={`polygon-${index}`} id={`polygon-${index}`} type="geojson" data={geojson}>
                            <Layer
                                id={`polygon-fill-${index}`}
                                type="fill"
                                paint={{
                                    'fill-color': !insideGeojson(geojson, location) ? '#02a8ea' : '#49db0f',
                                    'fill-opacity': 0.6
                                }}
                            />
                            <Layer
                                id={`polygon-label-${index}`}
                                type="symbol"
                                layout={{
                                    'text-field': zone.name,
                                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                                    'text-size': 16,
                                    'text-offset': [0, 0],
                                    'text-anchor': 'center'
                                }}
                                paint={{
                                    'text-color': '#ffffff',
                                    'text-halo-color': '#333',
                                    'text-halo-width': 1,
                                    'text-halo-blur': 1
                                }}
                            />
                            <Layer
                                id={`polygon-outline-${index}`}
                                type="line"
                                paint={{
                                    'line-color': !insideGeojson(geojson, location) ? '#0f698d' : '#32970b',
                                    'line-width': 3,
                                }}
                            />
                        </Source>
                    );
                })}
                {chase && chase.coords && chase.coords[0] && chase.status === 'started' && useBonus &&
                    <Marker latitude={chase.coords[0].lat} longitude={chase.coords[0].lng}style={{ zIndex:10}}>
                        <div className="pulse-red shadow-map" style={{ background: '#f5000bcc', filter:"blur(1px)", zIndex:10,  borderRadius: '50%', width: '25px', height: '25px',  transform: 'translate(-50%, -100%)' }} />
                    </Marker>
                }
                {chase && chase.bonus && chase.bonus[0] && chase.status === 'started' &&
                    <Marker latitude={chase.bonus[0].lat} longitude={chase.bonus[0].lng} style={{ zIndex:10}}>
                        <Box position="relative" >
                            <TiStarFullOutline
                            color={"#ffffff"}
                            className="bonus"
                            style={{ transform:  'translate(0, -100%)',position: "absolute", filter:"blur(2px)", top:"-4px", left:"-3px",fontSize: "51px", zIndex: 9, margin: "auto" }}
                            />
                            <TiStarFullOutline
                            color={"#ffbf00"}
                            className="bonus shadow-map "
                            style={{ transform:  'translate(0, -100%)',position: "absolute", top:0, left:0, fontSize: "45px", zIndex: 10, margin: "auto" }}
                            />
                        </Box>
                    </Marker>
                }
            </>
        );
    };*/

    const renderPolygons = useMemo((): ReactElement | undefined => {
        if (!myZones || myZones.length === 0) return undefined;
        return (
          <>
            {/*myZones.map((zone, index) => {
              if (!zone || !zone.coords || zone.coords.length < 3) return null;
      
              const polygon = zone.coords.map(coord => [coord.lng, coord.lat]);
              const geojson = {
                type: 'Feature',
                properties: { title: 'polygon' },
                geometry: {
                  type: 'Polygon',
                  coordinates: [[...polygon, polygon[0]]] // Fermer le polygone en revenant au point de départ
                }
              };*/}
      
            {geoJson && geoJson.map(({zoneId, geojson}, index) => {
              return (
                <Source key={`polygon-${index}`} id={`polygon-${index}`} type="geojson" data={geojson}>
                  <Layer
                    id={`polygon-fill-${index}`}
                    type="fill"
                    paint={{
                      'fill-color': !insideGeojson(geojson, location) ? '#02a8ea' : '#49db0f',
                      'fill-opacity': 0.6
                    }}
                  />
                  <Layer
                    id={`polygon-label-${index}`}
                    type="symbol"
                    layout={{
                      'text-field': geojson.properties.title,
                      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                      'text-size': 16,
                      'text-offset': [0, 0],
                      'text-anchor': 'center'
                    }}
                    paint={{
                      'text-color': '#ffffff',
                      'text-halo-color': '#333',
                      'text-halo-width': 1,
                      'text-halo-blur': 1
                    }}
                  />
                  <Layer
                    id={`polygon-outline-${index}`}
                    type="line"
                    paint={{
                      'line-color': !insideGeojson(geojson, location) ? '#0f698d' : '#32970b',
                      'line-width': 3
                    }}
                  />
                </Source>
              );
            })}
            {chase && chase.coords && chase.coords[0] && chase.status === 'started' && useBonus && (
              <Marker latitude={chase.coords[0].lat} longitude={chase.coords[0].lng} style={{ zIndex: 10 }}>
                <div className="pulse-red shadow-map" style={{ background: '#f5000bcc', filter: 'blur(1px)', zIndex: 10, borderRadius: '50%', width: '25px', height: '25px', transform: 'translate(-50%, -100%)' }} />
              </Marker>
            )}
            {chase && chase.bonus && chase.bonus[0] && chase.status === 'started' && (
              <Marker latitude={chase.bonus[0].lat} longitude={chase.bonus[0].lng} style={{ zIndex: 10 }}>
                <Box position="relative" style={{transform: 'translate(0, -50px)'}}>
                  <TiStarFullOutline
                    color={"#ffffff"}
                    className="bonus"
                    style={{ position: "absolute", filter: "blur(2px)", top: "-4px", left: "-3px", fontSize: "51px", zIndex: 9, margin: "auto" }}
                  />
                  <TiStarFullOutline
                    color={"#ffbf00"}
                    className="bonus shadow-map"
                    style={{ position: "absolute", top: 0, left: 0, fontSize: "45px", zIndex: 10, margin: "auto" }}
                  />
                </Box>
              </Marker>
            )}
            {chase && chase.status !== 'started' && allPlayersPositions && allPlayersPositions.map((player, index) => (
              <Marker longitude={player.lng} latitude={player.lat} key={"allPlayer"+index}>
              <div style={{ background:'#ff9300', border:'3px solid #ff9300', borderRadius: '50%',borderBottomRightRadius:0,transform: 'rotate(45deg)translate(-50%, -50%)' }}><Avatar
                  src={player?.photoUrl as string}
                  size={"xs"}
                  icon={undefined}
                  name={player?.name as string}
                  bg="black"
                  style={{transform: 'rotate(-45deg)'}}
              /></div>
              </Marker>
            ))}

            {chase && chase.status !== 'started' && interestingPlaces && interestingPlaces.map((place, index) => (
              <Marker longitude={place.coordinates[0]} latitude={place.coordinates[1]} key={"allPlaces"+index}>
              <div style={{ background:'white', borderRadius:'50%', transform: 'translate(-50%, -50%)' }}>
                <FaInfoCircle style={{color:place.discovered?"black":"#27cf1c", width:"24", height:"24"}}/>
              </div>
              </Marker>
            ))}

            {chase && chase.status !== 'started' && allItems && allItems.map((item, index) => {
              // Calcul de la date actuelle
            const now = Date.now();
            const expiresAt = item.expiresAt || new Date().getTime() + 24 * 60 * 60 * 1000; // Date d'expiration en millisecondes
            
            // Calcul de la progression en fonction de l'expiration
            const totalDuration = 24 * 60 * 60 * 1000; // 24 heures en millisecondes
            const elapsedTime = Math.min(Math.max(expiresAt - now, 0), totalDuration); // Temps écoulé en millisecondes
            
            let alreadyGetArtifact = false
            let character:any = null
            if ( item.type == "character") {
              character = allInGameCharactersConst.find(c=>c.id===item.character)
              const quest = AllQuestModelConst.find(q=>q.id===character?.questId)
              if ( quest && quest.artifact && quest.artifact.id ){
                const check = myArtifacts?.find(a=>a.id===quest.artifact?.id)
                if ( check ){
                  alreadyGetArtifact = true
                }
              }
            }
            // Calcul du pourcentage de progression (de 0 à 100)
            const progressPercentage = (elapsedTime / totalDuration) * 100;
            if ( item.type !== "character" ) {
              return (
              <Marker longitude={item.lng} latitude={item.lat} key={"allItems"+index} >
                <div style={{ background:"#000000b3", color:"white",border:'3px solid #e54fa0', position:"relative", borderRadius: '50%',transform: 'translate(-50%, -50%)' }}>
                  {item.type == "eggs" &&<FaEgg style={{color:isRaining?"#0d6bdf":"white", width:"32", height:"32", animation: "bounce 1s ease-in infinite"}}/>}
                  {item.type == "food" &&<GiCupcake style={{width:"32", height:"32", animation: "bounce 1s ease-in infinite"}}/>}                  
                  {item.type == "feather" &&<FaFeather style={{color:"white" , width:"32", height:"32", animation: "bounce 1s ease-in infinite"}}/>}                                    
                  <Progress position="absolute" w="50px" bgColor="#000000b3" top="-11px" left="-10px" value={progressPercentage} size='xs' colorScheme="pink" />
                </div>
              </Marker>
            )}
            if (!user) return null;
            return (
              <Marker onClick={()=>{showCharacterDetail(character?.id)}} longitude={item.lng} latitude={item.lat} key={"allItems"+index} style={{zIndex: 11 }}>
                <div style={{ background:"#000000b3", color:"white",border:'3px solid white', position:"relative", borderRadius: '50%',transform: 'translate(-50%, -50%)', }}>
                   {item.type == "character" && <Box position="relative">
                    <Avatar
                      src={character?.url as string}
                      size={"md"}
                      icon={undefined}
                      name={character?.name}
                      bg="black"
                      /> 
                    {!alreadyGetArtifact && <Badge style={{ animation: "pulse-red 1s ease-in infinite"}} bg="red" color="white" position="absolute" bottom="0" right="0" zIndex={1} >1</Badge>}
                    </Box>
                    }                
                </div>
              </Marker>
            )
            })}
          </>
        );
      }, [myZones, geoJson, chase, useBonus, allPlayersPositions, allItems, interestingPlaces]);
      
      const accessibleZones = useMemo(() => {
        const layersToCheck = ['landuse', 'place', 'poi_label', 'park'];
        const buildingLayer = 'building'; // Couche pour vérifier la présence de bâtiments
      
        const newAccessibleZones: ZoneType[] = [];
      
        layersToCheck.forEach((layer) => {
          if (!(mapRef && mapRef.current)) return;
      
          const map = mapRef.current.getMap();
          const features = map.querySourceFeatures('composite', {
            sourceLayer: layer,
          });
      
          features.forEach((feature: any) => {
            if (!feature.geometry.coordinates[0] || !feature.geometry.coordinates[0].map) return;
      
            const featureCoords = validatePolygonIfNeeded(
              feature.geometry.coordinates[0].map((coord: any) => ({
                lat: coord[1],
                lng: coord[0],
              }))
            );
      
            const isAccessible =
              (layer === 'landuse' && feature.properties.class === 'park') ||
              (layer === 'place' &&
                (feature.properties.class === 'square' || feature.properties.class === 'pedestrian')) ||
              (layer === 'poi_label' &&
                (feature.properties.class === 'playground' || feature.properties.class === 'garden')) ||
              (layer === 'park' &&
                (feature.properties.class === 'park' || feature.properties.class === 'garden'));
      
            if (isAccessible && featureCoords) {
              const isInMyZones = doesZoneOverlapWithExisting(featureCoords, myZones);
              const doesOverlapWithAccessibleZones = doesZoneOverlapWithExisting(
                featureCoords,
                newAccessibleZones
              );
      
              if (!isInMyZones && !doesOverlapWithAccessibleZones) {
                // Vérifier l'absence de bâtiments dans la zone
                let buildingsInZone = [];
                if (feature.geometry.coordinates[0].length >= 3) {
                  try {
                    buildingsInZone = map.querySourceFeatures('composite', {
                      sourceLayer: buildingLayer,
                      filter: ['within', turf.polygon([feature.geometry.coordinates[0]])], // Utiliser Turf.js pour vérifier si un bâtiment est à l'intérieur
                    });
                  }catch(e){
                      if ( process.env.NODE_ENV==="development" ) {
                          console.log(e)
                      }
                  }
                }
      
                if (buildingsInZone.length === 0) {
                  // Ajouter la zone accessible si aucune zone ne chevauche les existantes
                  newAccessibleZones.push({
                    id: feature.geometry.coordinates[0][0][0] + '-' + feature.geometry.coordinates[0][0][1],
                    ownerId: 'generic',
                    shared: 'public',
                    name: capitalizeFirstLetter(feature.properties.type) || 'Unnamed Zone',
                    city: feature.properties.city || '',
                    country: feature.properties.country || '',
                    lat: featureCoords[0].lat,
                    lng: featureCoords[0].lng,
                    date: new Date(),
                    coords: featureCoords,
                  });
                }
              }
            }
          });
        });
      
        return newAccessibleZones;
      }, [mapRef, myZones]);

    const checkZones = ()=>{
        setMapboxLoaded(true)
        setDoResize(true)  
        if ( mapRef && mapRef.current && mapRef.current.getZoom() > 12 && ((!myZones || myZones.length == 0)||(myZones && myZones.length < 100))){
          setMyZones((prevZones) => {
              const newZones = accessibleZones.reduce((acc, zone) => {
                  if (!prevZones.find((z) => z.id === zone.id)) {
                      acc.push(zone);
                  }

                  return acc;
              }, [...prevZones]);

              return newZones;
          })
        }
    }

    return (
        <GameContext.Provider value={{
            mapRef: mapRef,
            chase,
            insideZone,
            isDarkMode,
            closeToMonster,
            startGame,
            renderPolygons: ()=>renderPolygons,
            checkZones,
            doResize,
            setDoResize,
            gotBonus,
            setUseBonus,
            elapsedTime
        }}>
            {props.children}
            <CharacterTalkingQuestModal isOpen={isOpenInGameCharacter} onClose={onCloseInGameCharacter} characterId={inFrontCharacter as string} />
            <CharacterDetailQuestModal isOpen={isOpenInGameCharacterDetail} onClose={hideCharacterDetail} characterId={characterDetail as string} />
        </GameContext.Provider>
    )
}